import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Shipping from '../../../_shared/models/Shipping';
import TransportModal from '../../Modal/TransportModal';
import Loading from '../../../_shared/components/Loading';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import BadgeWithTooltip from '../../../_shared/components/BadgeWithTooltip';
import ROUTES from '../../Routes/constants';
import { BidSource } from '../../../_shared/types/auction';
import TradeRevDirect from '../../Trd';
import StyledAnchorLink from '../../../_shared/components/AnchorLink';
import useAnalytics, {
  getVinBasicProperties,
  getVinLaunchProperties,
  getSellerProperties
} from '../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';

import {
  StyledTransportSection,
  StyledTransportSeller,
  StyledTransportInfo,
  StyledTransportMessage,
  StyledRateInfo,
  StyledInformationIcon,
  StyledPromoOnIndicator,
  StyledPromoOnIndicatorLabel,
  StyledTransportValue,
  StyledTransportMessageNone,
  StyledDealer,
  DealerProfileText,
  StyledRatings,
  StyledIcon,
  StyledRatingsCount,
  StyledNoRatings
} from './Styles';

const propTypes = {
  variant: PropTypes.oneOf(['promo', 'standard']),
  shippingDetails: PropTypes.shape(Shipping),
  transportInfo: PropTypes.any,
  tradeId: PropTypes.number,
  dealerInfo: PropTypes.any,
  ratingsCount: PropTypes.any,
  isSameTrade: PropTypes.bool
};

const defaultProps = {
  variant: 'standard',
  shippingDetails: undefined,
  transportInfo: undefined,
  tradeId: undefined,
  dealerInfo: undefined,
  ratingsCount: undefined,
  isSameTrade: false
};

const TransportInfo = ({
  variant,
  shippingDetails,
  transportInfo,
  tradeId,
  dealerId,
  dealerInfo,
  ratingsCount,
  badges,
  isSameTrade,
  isAtAdesa,
  isOMP,
  isFetchingEstimate,
  isMultiOrgAndEnabled,
  tradeDetails
}) => {
  const track = useAnalytics();
  const rating = (Math.round(dealerInfo?.rating * 10 * 5) / 10).toFixed(1);
  const transportMessageAvailable =
    shippingDetails &&
    shippingDetails.shippingQuoteFormatted &&
    shippingDetails.distance &&
    shippingDetails.distance !== undefined &&
    shippingDetails.distance !== null &&
    transportInfo;
  const isValidTransportEstimate =
    transportMessageAvailable &&
    shippingDetails.shippingQuoteFormatted !== 'No quote available' &&
    transportInfo.pickupLocation;

  const location = window.location.href.includes(ROUTES.BUYING)
    ? BidSource.MINI_VDP
    : BidSource.VDP_OVERLAY;

  const transportMessage = isValidTransportEstimate ? (
    <FormattedMessage
      id="vdp.transport.estimate"
      values={{
        startsWithVowel: !!transportInfo.pickupLocation.city?.match(
          /^[aeiou].*/i
        ),
        city: transportInfo.pickupLocation.city,
        province: transportInfo.pickupLocation.province
      }}
    />
  ) : (
    <FormattedMessage id="vdp.transport.estimateNotAvailable" />
  );

  const transportValue = isValidTransportEstimate
    ? `${convertToCurrency(
        shippingDetails.shippingQuoteFormatted.replace(/\$/g, '')
      )} / ${shippingDetails.distance} `
    : '';
  const [modalIsOpen, setIsOpen] = useState(false);
  const openModal = () => {
    const sourcePrefix = location === BidSource.MINI_VDP ? 'mini-' : '';
    setIsOpen(true);

    if (isOMP) {
      track(SegmentConfig.names.vdpCheckTransportInfo, {
        ...getSellerProperties(tradeDetails),
        ...getVinBasicProperties(tradeDetails),
        ...getVinLaunchProperties(tradeDetails)
      });
    } else {
      track(MixpanelConfig.names.transportRateInfo, {
        promo_available: variant === 'promo',
        source: `${sourcePrefix}VDP-overview`
      });
    }
  };
  const closeModal = () => {
    setIsOpen(false);
  };
  const trackDealerProfileView = () => {
    track(MixpanelConfig.names.viewDealerProfilePage, {
      source: `transport info - ${location}`
    });
  };

  const trackDealerProfileViewOmp = () => {
    track(SegmentConfig.names.viewDealerProfilePage, {
      ...getVinBasicProperties(tradeDetails),
      ...getSellerProperties(tradeDetails)
    });
  };

  return isFetchingEstimate || !transportMessageAvailable || !isSameTrade ? (
    <StyledTransportSection
      data-testid="vdpTransportInfoLoading"
      variant={variant}
    >
      <Loading height="40px" />
    </StyledTransportSection>
  ) : (
    <StyledTransportSection
      id="vdpTransportInfo"
      variant={variant}
      data-testid="vdp-transport-info"
    >
      <StyledTransportSeller>
        {dealerInfo?.appraisedBy &&
        dealerInfo?.address &&
        rating &&
        dealerInfo?.id &&
        Number.isInteger(ratingsCount) ? (
          <>
            <StyledDealer>
              <BadgeWithTooltip badges={badges} />
              {tradeId &&
                (!isOMP ? (
                  <StyledAnchorLink
                    href={`${process.env.REACT_APP_TR4_ENDPOINT}profile/${tradeId}/dealer/${dealerInfo?.id}`}
                    onClick={trackDealerProfileView}
                    data-testid="vdp-dealer-profile-info"
                  >
                    <DealerProfileText>
                      {dealerInfo?.appraisedBy.companyName}
                    </DealerProfileText>
                  </StyledAnchorLink>
                ) : (
                  <StyledAnchorLink
                    href={`${process.env.REACT_APP_OMP_ENDPOINT}profile/${tradeId}/dealer/${dealerInfo?.id}`}
                    onClick={trackDealerProfileViewOmp}
                    data-testid="vdp-dealer-profile-info"
                  >
                    <DealerProfileText data-testid="vdp-dealer-profile-info">
                      {dealerInfo?.appraisedBy.companyName}
                    </DealerProfileText>
                  </StyledAnchorLink>
                ))}
              {ratingsCount > 0 ? (
                <StyledRatings>
                  {rating}
                  <StyledIcon icon="TR5-Icon-Star-Filled" />
                  <StyledRatingsCount>
                    {/* # ratings */}
                    <FormattedMessage
                      id="vdp.seller.ratings"
                      values={{ ratingsCount }}
                    />
                  </StyledRatingsCount>
                </StyledRatings>
              ) : (
                <StyledNoRatings>
                  {/* (No ratings yet) */}
                  <FormattedMessage id="vdp.seller.noRatings" />
                </StyledNoRatings>
              )}
            </StyledDealer>
            <StyledRateInfo id="vdpRateInfo" onClick={openModal}>
              <StyledInformationIcon />
              {variant === 'promo' ? (
                <FormattedMessage id="vdp.transport.PromoInfo" />
              ) : (
                <FormattedMessage id="vdp.rateInfo" />
              )}
            </StyledRateInfo>
            <TransportModal
              closeModal={closeModal}
              data={shippingDetails}
              dealerId={dealerId}
              isMultiOrgAndEnabled={isMultiOrgAndEnabled}
              modalIsOpen={modalIsOpen}
              tradeId={tradeId}
            />
          </>
        ) : (
          <Loading width="250px" height="20px" />
        )}
      </StyledTransportSeller>
      <StyledTransportInfo variant={variant} data-testid="vdpTransportInfo">
        {variant === 'promo' ? (
          <StyledPromoOnIndicator>
            <StyledPromoOnIndicatorLabel>
              <FormattedMessage id="vdp.transportPromoOn" />
            </StyledPromoOnIndicatorLabel>
          </StyledPromoOnIndicator>
        ) : (
          <></>
        )}
        <TradeRevDirect
          shippingDetails={shippingDetails}
          testId="trd-vdp"
          displayType="vdp"
          dealer={dealerInfo}
          isValidTransportEstimate={isValidTransportEstimate}
          isAtAdesa={isAtAdesa}
        />
        {!isValidTransportEstimate ? (
          <StyledTransportMessageNone>
            Transport estimate not available for this vehicle right now
          </StyledTransportMessageNone>
        ) : (
          <StyledTransportMessage variant={variant}>
            {transportMessage}{' '}
            <StyledTransportValue>{transportValue}</StyledTransportValue>
            (<FormattedMessage id="vdp.transportValueEstimated" />)
          </StyledTransportMessage>
        )}
      </StyledTransportInfo>
    </StyledTransportSection>
  );
};

TransportInfo.prototype = propTypes;
TransportInfo.defaultProps = defaultProps;

export default TransportInfo;
